// ── Optimization Center: Listing Quality Scoring Module ──────────
// Owns: step-scoring panel DOM. Step04 owns the single diagnostic API flow.

import {
  getState,
  setState,
  subscribe,
} from './state.js';
import { DIMENSION_LABELS } from './constants.js';

const $ = (selector) => document.querySelector(selector);

function createScoringElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function replaceScoringChildren(root, ...children) {
  if (!root) return;
  root.replaceChildren(...children.filter(Boolean));
}

function scoreTone(percent) {
  return Number(percent) >= 80 ? 'high' : Number(percent) >= 50 ? 'medium' : 'low';
}

function scoreDimensionLabel(key) {
  if (key === 'contentQuality') return '结构完整度';
  if (key === 'semanticQuality') return '语义质量';
  if (key === 'seoExecution') return 'SEO 执行度';
  return DIMENSION_LABELS[key] || key;
}

function getDimensionChecks(dimension) {
  const candidates = [dimension.checks, dimension.deductions, dimension.details];
  return candidates.find((checks) => Array.isArray(checks) && checks.length) || candidates.find(Array.isArray) || [];
}

function createScoreValue(total, max, percent, className = 'scoring-score-value') {
  const value = createScoringElement('strong', `${className} scoring-tone-${scoreTone(percent)}`);
  value.append(
    document.createTextNode(String(total)),
    createScoringElement('small', 'scoring-score-suffix', `/${max}`),
  );
  return value;
}

function createDimensionCard(key, dimension) {
  const label = scoreDimensionLabel(key);
  const card = createScoringElement('div', 'score-dim-card');
  if (!dimension || dimension.score == null) {
    card.append(
      createScoringElement('div', 'scoring-dimension-label', label),
      createScoringElement('div', 'scoring-dimension-unrated', '未评估'),
      createScoringElement('div', 'scoring-dimension-reason', dimension?.reason || '缺少可验证输入'),
    );
    return card;
  }

  const score = Number(dimension.score || 0);
  const maxScore = Math.max(Number(dimension.maxScore || 0), 1);
  const percentage = Math.round((score / maxScore) * 100);
  const boundedPercentage = Math.max(0, Math.min(100, percentage));
  const tone = scoreTone(percentage);
  const checks = getDimensionChecks(dimension);
  const passed = checks.filter((item) => item.passed).length;
  const deductionTotal = Number(dimension.deductionTotal || 0);
  const header = createScoringElement('div', 'scoring-dimension-header');
  header.append(
    createScoringElement('span', 'scoring-dimension-label', label),
    createScoringElement('span', `scoring-dimension-score scoring-tone-${tone}`, `${score}/${maxScore}`),
  );
  const progress = createScoringElement('progress', `scoring-dimension-progress scoring-tone-${tone}`);
  progress.max = 100;
  progress.value = boundedPercentage;
  const semanticStatus = key === 'contentQuality' && dimension.semanticReviewStatus === 'not_evaluated'
    ? '，语义质量未评估'
    : '';
  card.append(
    header,
    progress,
    createScoringElement('div', 'scoring-dimension-summary', `${passed}/${checks.length} 项通过${deductionTotal > 0 ? `，扣 ${deductionTotal} 分` : ''}${semanticStatus}`),
  );
  return card;
}

function createSuggestionCard(suggestion) {
  const priority = ['high', 'medium', 'low'].includes(suggestion.priority) ? suggestion.priority : 'low';
  const card = createScoringElement('div', 'suggestion-card');
  card.append(
    createScoringElement('span', `scoring-suggestion-priority scoring-tone-${priority}`, `[${suggestion.changeClass || suggestion.priority || 'optional'}]`),
    createScoringElement('strong', '', suggestion.dimension || ''),
  );
  if (suggestion.field) card.append(createScoringElement('span', 'scoring-suggestion-meta', `· ${suggestion.field}`));
  if (suggestion.source) card.append(createScoringElement('span', 'scoring-suggestion-meta', `· ${suggestion.source}`));
  card.append(document.createTextNode(`: ${suggestion.message || suggestion.issue || suggestion.recommendedAction || ''}`));
  return card;
}

function renderScoringResult() {
  const report = getState('scoringReport');
  if (!report) return;

  $('#scoring-result').hidden = false;
  const total = report.totalScore || 0;
  const max = report.maxScore || 100;
  const percentage = Math.round((total / max) * 100);
  const qualityGate = report.qualityGate || null;
  const totalRoot = $('#scoring-total');

  if (qualityGate) {
    const explanation = createScoringElement('div', 'scoring-explanation');
    const scoreBlock = document.createElement('div');
    scoreBlock.append(
      createScoringElement('span', '', '当前原稿综合质量'),
      createScoreValue(total, max, percentage),
    );
    explanation.append(
      scoreBlock,
      createScoringElement('p', '', '这是最近一次原稿诊断分；生成候选稿不会立即产生新分数，写回后重新诊断才会更新。'),
    );
    replaceScoringChildren(totalRoot, explanation);
  } else {
    const legacy = createScoringElement('div', 'scoring-total-legacy');
    const value = createScoringElement('div', `scoring-total-legacy-value scoring-tone-${scoreTone(percentage)}`);
    value.append(document.createTextNode(String(total)), createScoringElement('span', 'scoring-total-legacy-suffix', `/${max}`));
    legacy.append(value, createScoringElement('div', 'scoring-total-legacy-label', '旧版综合评分'));
    replaceScoringChildren(totalRoot, legacy);
  }

  const dimensions = qualityGate
    ? {
        contentQuality: qualityGate.contentQuality,
        semanticQuality: report.semanticReview
          ? {
              score: report.semanticReview.overallScore,
              maxScore: 100,
              status: report.semanticReview.status,
              checks: report.semanticReview.issues || [],
              reason: report.semanticReview.summary,
            }
          : null,
        seoExecution: qualityGate.seoExecution,
      }
    : (report.dimensions || {});
  replaceScoringChildren($('#scoring-dimensions'), ...Object.entries(dimensions).map(([key, dimension]) => createDimensionCard(key, dimension)));

  const suggestions = report.suggestions || [];
  const suggestionRoot = $('#scoring-suggestions');
  if (!suggestions.length) {
    replaceScoringChildren(suggestionRoot);
    return;
  }
  const details = createScoringElement('details', 'diagnostic-raw-copy');
  details.append(createScoringElement('summary', '', `查看模型原始评分建议（${suggestions.length} 项）`));
  const body = createScoringElement('div', 'diagnostic-raw-copy-body');
  body.append(...suggestions.map(createSuggestionCard));
  details.append(body);
  replaceScoringChildren(suggestionRoot, details);
}

export function initStep5() {
  $('#step5-prev-button')?.addEventListener('click', () => setState('currentStep', 4));

  subscribe('state:scoringReportChanged', () => {
    renderScoringResult();
  });

  // Step04 owns the only diagnostic orchestration. This module renders state
  // only; restoring or changing a draft must not start another scoring request.
  if (getState('scoringReport')) renderScoringResult();
}
